// de.ts — German catalog for the owner dashboard.
//
// Translated per docs/superpowers/specs/locale-translator-contract.md (binding tone, do-not-
// translate list, plural rule) and docs/superpowers/specs/locale-glossary-vi.md §2 (do-not-
// translate list, imported by reference). Register: "du" throughout — see
// .superpowers/sdd/translator-de-report.md for the formality decision and judgment calls.
import type { Catalog } from './en';

export const de = {
  common: {
    close: 'Schließen',
  },
  status: {
    pending: 'ausstehend',
    crawling: 'wird gescannt',
    indexing: 'wird indexiert',
    ready: 'bereit',
    error: 'Fehler',
    paused: 'pausiert',
    hint: 'Hinweis',
    deleting: 'wird gelöscht',
  },
  time: {
    never: 'nie',
    justNow: 'gerade eben',
    inAMoment: 'gleich',
    secondsAgo: ({ n }: { n: number }) => `vor ${n}s`,
    inSeconds: ({ n }: { n: number }) => `in ${n}s`,
    minutesAgo: ({ n }: { n: number }) => `vor ${n} Min`,
    inMinutes: ({ n }: { n: number }) => `in ${n} Min`,
    hoursAgo: ({ n }: { n: number }) => `vor ${n} Std`,
    inHours: ({ n }: { n: number }) => `in ${n} Std`,
    daysAgo: ({ n }: { n: number }) => `vor ${n} T`,
    inDays: ({ n }: { n: number }) => `in ${n} T`,
    monthsAgo: ({ n }: { n: number }) => `vor ${n} Mon`,
    inMonths: ({ n }: { n: number }) => `in ${n} Mon`,
    yearsAgo: ({ n }: { n: number }) => `vor ${n} J`,
    inYears: ({ n }: { n: number }) => `in ${n} J`,
  },
  shell: {
    impersonating: ({ email }: { email: string }) =>
      `Angezeigt als ${email} — du gibst dich als dieser Nutzer aus.`,
    stopImpersonating: 'Beenden',

    platformAdmin: 'Plattform-Admin',
    adminTabWorkspaces: 'Workspaces',
    adminTabBots: 'Bots',
    adminTabJobs: 'Jobs',
    adminTabPosts: 'Beiträge',
    adminTabFeedback: 'Feedback',
    adminTabPlayground: 'Playground',

    botsLoadError: 'Deine Bots konnten nicht geladen werden.',
    botsLoadErrorInline: 'Deine Bots konnten nicht geladen werden',
    reloadToRetry: 'Lad die Seite neu, um es erneut zu versuchen.',

    navOverview: 'Übersicht',
    navUnanswered: 'Unbeantwortet',
    navConversations: 'Gespräche',
    navLeads: 'Leads',
    navSources: 'Quellen',
    navPlayground: 'Playground',
    navAppearance: 'Design',
    navSettings: 'Einstellungen',
    groupNeedsYou: 'BRAUCHT DICH',
    groupKnowledge: 'WISSEN',
    groupSetup: 'EINRICHTUNG',

    closeMenu: 'Menü schließen',
    navigationLabel: 'Navigation',
    openNavigation: 'Navigation öffnen',
    openAccountMenu: 'Kontomenü öffnen',

    switchBotAriaLabel: ({ name }: { name: string }) => `${name}, Bot wechseln`,
    botSwitcherHeader: ({ n, cap }: { n: number; cap: number | null }) =>
      `DEINE BOTS${cap !== null ? ` · ${n} VON ${cap} GENUTZT` : ''}`,
    allBots: 'Alle Bots',
    newBot: 'Neuer Bot',
    newBotOfCapUsed: ({ n, cap }: { n: number; cap: number }) => `Neuer Bot — ${n} von ${cap} genutzt`,

    pagesIndexed: 'Indexierte Seiten',

    feedbackTrigger: 'Feedback',
    feedbackTypeGroupLabel: 'Feedback-Art',
    feedbackTypeBug: 'Fehler',
    feedbackTypeFeature: 'Feature-Wunsch',
    feedbackTypeQuestion: 'Frage',
    feedbackPlaceholder: 'Was ist passiert, oder was würde Embent für dich besser machen?',
    feedbackSent: 'Gesendet — wir lesen jedes.',
    feedbackSendError: 'Feedback konnte nicht gesendet werden.',
    feedbackSending: 'Wird gesendet…',
    feedbackSend: 'Senden',
    feedbackDialogTitle: 'Feedback senden',
    feedbackDialogDescription: 'Fehler, Feature-Wunsch oder Frage — geht direkt an das Team.',

    workspaceSettings: 'Workspace-Einstellungen',
    members: 'Mitglieder',
    billingUsage: 'Abrechnung & Nutzung',
    switchWorkspace: 'Workspace wechseln',
    accountLabelPrefix: 'Konto — ',
    account: 'Konto',
    signOut: 'Abmelden',
    themePrefix: 'Design: ',
    languagePrefix: 'Sprache: ',
    nextPrefix: 'nächste: ',
    themeSystem: 'System',
    themeDark: 'Dunkel',
    themeLight: 'Hell',
    roleOwner: 'Inhaber',
    roleAdmin: 'Admin',
    roleMember: 'Mitglied',
    workspaceSubline: ({ plan, role }: { plan: string | null; role: string }) =>
      plan ? `${plan} · du bist ${role}` : `Du bist ${role}`,
    workspaceMenuAriaLabel: ({ name }: { name: string }) => `${name}, Workspace- und Kontomenü`,

    usageWarning: ({ pct }: { pct: number }) =>
      `Du hast ${pct}% deiner monatlichen Nachrichten genutzt — upgrade, damit jedes Gespräch weiterläuft.`,
    dismiss: 'Verwerfen',

    renewsOn: ({ date }: { date: string }) => `Verlängert sich am ${date}`,
    resetsOn: ({ date }: { date: string }) => `Setzt sich zurück am ${date}`,
    answersPaused: 'Neue Antworten pausiert — über 110% des Limits',
    messagesLeft: ({ n }: { n: string }) => `${n} Nachrichten diesen Monat übrig`,
    badgeStillShown: ({ plan }: { plan: string }) => `${plan} zeigt weiterhin das "Powered by Embent"-Badge`,
    badgeOptional: ({ plan }: { plan: string }) => `${plan} macht das "Powered by Embent"-Badge optional`,
    upgradeCta: ({ price }: { price: number }) => `Upgrade — $${price}/Mon.`,
    resumeAnswersCta: ({ price }: { price: number }) => `Antworten fortsetzen — $${price}/Mon.`,
    resumeAnswersBenefit: 'Ein Upgrade setzt Antworten sofort fort',
    upgradeBenefit: ({
      plan,
      messages,
      bots,
      seats,
    }: {
      plan: string;
      messages: string;
      bots: number;
      seats: number;
    }) => `${plan} fügt ${messages} Nachrichten, ${bots} Bots und ${seats} ${seats === 1 ? 'Sitzplatz' : 'Sitzplätze'} hinzu`,
  },
  bots: {
    noSources: 'keine Quellen',
    allSourcesReady: ({ n }: { n: number }) => `${n} · alle bereit`,
    sourceStatusRollup: ({ n, status }: { n: number; status: string }) => `${n} · ${status}`,
    paused: 'Pausiert',
    sourcesRemoving: ({ n }: { n: number }) => `${n} ${n === 1 ? 'Quelle' : 'Quellen'} · wird entfernt`,
    sourcesSyncError: ({ n }: { n: number }) => `${n} ${n === 1 ? 'Quelle' : 'Quellen'} · Sync-Fehler`,
    sourcesSyncing: ({ n }: { n: number }) => `${n} ${n === 1 ? 'Quelle' : 'Quellen'} · synchronisiert`,
    messagesUnanswered: ({ messages, unanswered }: { messages: number; unanswered: number }) =>
      `${messages} ${messages === 1 ? 'Nachricht' : 'Nachrichten'} · ${unanswered} unbeantwortet`,
    botsCount: ({ n }: { n: number }) => `${n} ${n === 1 ? 'Bot' : 'Bots'}`,
    botsOfCapUsed: ({ n, cap }: { n: number; cap: number }) => `${n} von ${cap} genutzt`,

    colBot: 'Bot',
    colStatus: 'Status',
    colMessages14d: 'Nachrichten · 14 Tage',
    colUnanswered: 'Unbeantwortet',
    colSources: 'Quellen',
    pageTitle: 'Bots',
    emptyTitle: 'Erstelle deinen ersten Bot',
    emptyDescription:
      'Richte ihn auf deine Website oder Doku — er durchsucht die Inhalte und beginnt, Besucherfragen zu beantworten.',
    planIncludesBots: ({ cap }: { cap: number }) => `Dein Plan enthält ${cap} ${cap === 1 ? 'Bot' : 'Bots'}`,
    upgradeGateDescription: ({ name, priceUsd, maxBots }: { name: string; priceUsd: number; maxBots: number }) =>
      `${name} ($${priceUsd}/Mon.) betreibt bis zu ${maxBots} Bots — einen pro Website, jeder mit eigenem Wissen und Look.`,
    comparePlansLink: 'Pläne vergleichen →',
    newBotDialogTitle: 'Neuer Bot',
    botNameFieldLabel: 'Name',
    botNamePlaceholder: 'Acme Support Bot',
    creatingCta: 'Wird erstellt…',
    createCta: 'Erstellen',
  },
  auth: {
    emailLabel: 'E-Mail',
    passwordLabel: 'Passwort',
    genericFormError: 'Etwas ist schiefgelaufen. Bitte versuch es erneut.',
    continueWithGoogle: 'Mit Google fortfahren',
    backToSignIn: 'Zurück zur Anmeldung',

    signInTitle: 'Anmelden',
    signInSubtitle: 'Willkommen zurück bei Embent.',
    passwordJustResetNotice: 'Passwort aktualisiert — melde dich mit deinem neuen Passwort an.',
    forgotPasswordLink: 'Passwort vergessen?',
    unverifiedResendNotice: 'Schau in dein Postfach — wir haben die Bestätigungs-E-Mail erneut gesendet.',
    signingIn: 'Anmeldung läuft…',
    signInCta: 'Anmelden',
    noAccountPrompt: 'Noch kein Konto? ',
    signUpLink: 'Registrieren',

    signUpTitle: 'Erstell dein Konto',
    signUpSubtitle: 'Beantworte Besucherfragen in Minuten.',
    nameLabel: 'Name',
    creatingAccount: 'Konto wird erstellt…',
    createAccountCta: 'Konto erstellen',
    haveAccountPrompt: 'Schon ein Konto? ',
    signInLink: 'Anmelden',

    checkEmailTitle: 'Überprüf deine E-Mails',
    checkEmailSubtitle: 'Wir haben dir einen Bestätigungslink gesendet. Er kommt normalerweise innerhalb einer Minute an.',
    checkEmailBody: 'Klick auf den Link in der E-Mail, um dein Konto zu aktivieren.',

    verifyFailedTitle: 'Bestätigung fehlgeschlagen',
    verifiedTitle: 'E-Mail bestätigt',
    verifyFailedBody: 'Dieser Bestätigungslink ist ungültig oder abgelaufen.',
    verifiedBody: 'Alles bereit — du wirst zu deinem Dashboard weitergeleitet…',

    requestResetTitle: 'Passwort zurücksetzen',
    requestResetSubtitlePending: 'Gib deine E-Mail-Adresse ein, und wir senden dir einen Link zum Zurücksetzen.',
    requestResetSubtitleSent: 'Falls diese E-Mail-Adresse ein Embent-Konto hat, haben wir einen Link zum Zurücksetzen gesendet.',
    requestResetBodySent: 'Schau in dein Postfach. Der Link läuft in 1 Stunde ab.',
    sendingResetLink: 'Wird gesendet…',
    sendResetLinkCta: 'Link zum Zurücksetzen senden',

    resetLinkInvalidTitle: 'Link ungültig',
    resetLinkInvalidBody: 'Diesem Link zum Zurücksetzen des Passworts fehlt sein Token, oder er wurde bereits verwendet.',
    requestNewLinkCta: 'Neuen Link anfordern',
    chooseNewPasswordTitle: 'Wähl ein neues Passwort',
    chooseNewPasswordSubtitle: 'Nimm eins, das du noch nicht verwendet hast.',
    newPasswordLabel: 'Neues Passwort',
    resetLinkExpiredFallback: 'Dieser Link zum Zurücksetzen ist ungültig oder abgelaufen.',
    savingPassword: 'Wird gespeichert…',
    saveNewPasswordCta: 'Neues Passwort speichern',

    inviteNotValidTitle: 'Einladung ungültig',
    goToEmbentLink: 'Zu Embent',
    inviteInvalidFallback: 'Diese Einladung ist nicht mehr gültig.',
    inviteTitle: ({ workspaceName }: { workspaceName: string }) => `Du bist zu ${workspaceName} eingeladen`,
    inviteDescription: ({ email }: { email: string }) =>
      `Gesendet an ${email} — melde dich an oder erstell ein Konto mit dieser E-Mail-Adresse, um anzunehmen.`,
    inviteSignInCta: 'Anmelden',
    inviteSignUpCta: 'Konto erstellen',
    wrongAccountTitle: 'Falsches Konto',
    signOutCta: 'Abmelden',
    inviteAcceptFallback: 'Beim Annehmen dieser Einladung ist etwas schiefgelaufen.',
    joiningWorkspaceTitle: ({ workspaceName }: { workspaceName: string }) => `Trete ${workspaceName} bei…`,
    joiningWorkspaceBody: 'Einen Moment.',

    eventLabels: {
      newLead: 'neuer Lead',
      unansweredDigest: 'Unbeantwortet-Zusammenfassung',
      crawlFailed: 'Scan fehlgeschlagen',
      weeklySummary: 'Wochenzusammenfassung',
    },
    unsubscribeLinkExpiredTitle: 'Dieser Link ist abgelaufen',
    unsubscribeLinkExpiredBody: 'Verwalte Benachrichtigungen in den Bot-Einstellungen im Dashboard.',
    openDashboardCta: 'Dashboard öffnen',
    unsubscribedTitle: 'Du hast dich abgemeldet',
    unsubscribedBody: ({ eventLabel, botName }: { eventLabel: string; botName: string | null }) =>
      `Du bekommst keine ${eventLabel}-E-Mails mehr für ${botName ?? 'diesen Bot'}. Du kannst sie in den Bot-Einstellungen wieder aktivieren.`,

    notFoundEyebrow: 'Seite nicht gefunden',
    notFoundTitle: 'Unter dieser Adresse gibt es nichts',
    notFoundBody: 'Der Link könnte veraltet sein, oder die Seite wurde verschoben.',
    notFoundCta: 'Zu deinen Bots',

    landingBotsErrorFallback: 'Beim Laden deiner Bots ist etwas schiefgelaufen.',
    landingGoToBotsCta: 'Zu deinen Bots →',
  },
  onboarding: {
    skipForNow: 'Jetzt überspringen',
    invalidWebsiteUrl: 'Gib eine gültige Website-URL ein, z. B. https://acme.vn',
    websiteUrlLabel: 'Website-URL',
    genericFallbackError: 'Etwas ist schiefgelaufen.',

    crawlDefaultHostname: 'deine Website',
    noSourceToReplace: 'Noch keine Quelle zum Ersetzen.',

    stepWebsite: 'Website',
    stepCrawl: 'Scan',
    stepTryIt: 'Testen',
    stepLook: 'Look',
    stepGoLive: 'Live gehen',
    stepMobileReadout: ({
      current,
      total,
      label,
    }: {
      current: number;
      total: number;
      label: string;
    }) => `Schritt ${current} von ${total} · ${label}`,
    stepProgressAriaLabel: ({
      current,
      total,
      label,
    }: {
      current: number;
      total: number;
      label: string;
    }) => `Onboarding-Fortschritt: Schritt ${current} von ${total}, ${label}`,
    stepTablistAriaLabel: 'Onboarding-Fortschritt',

    websiteHeading: 'Füg deine Website hinzu',
    websiteSubtitle:
      'Embent liest deine Website und baut einen Bot, der mit Quellenangaben antwortet — nichts erfunden. Die meisten Websites sind in wenigen Minuten bereit.',
    botNameLabel: 'Bot-Name',
    botNamePlaceholder: 'Acme Support',
    websiteUrlPlaceholder: 'acme.vn',
    websiteUrlHelper: 'Wir finden deine Seiten automatisch. Du kannst später weitere Quellen hinzufügen.',
    botNameRequired: 'Gib einen Namen für deinen Bot ein.',
    websiteStarting: 'Startet…',
    websiteSubmitCta: 'Meine Website lesen lassen',

    crawlHeading: ({ hostname }: { hostname: string }) => `${hostname} wird gelesen…`,
    crawlSubtitleReady: 'Fertig — jede Seite ist indexiert und bereit zu antworten.',
    crawlSubtitleSyncing: 'Du musst hier nicht warten — probier deinen Bot aus, während er fertig wird.',
    crawlStatusLines: {
      pending: ['In Warteschlange — startet gleich…', 'Wird zum Start vorbereitet…'],
      crawling: ['Seiten auf der Website werden gefunden…', 'Links werden verfolgt…', 'Seiten werden gelesen…'],
      indexing: ['Suchindex wird erstellt…', 'Gelesenes wird verarbeitet…'],
    },
    crawlPagesIndexed: ({ n }: { n: number }) => `${n} Seiten indexiert`,
    crawlNoSourceYet: 'Wir konnten noch keine Quelle für diesen Bot finden.',
    crawlErrorLead:
      'Die wahrscheinlichste Ursache ist ein Tippfehler in der Adresse — überprüf sie unten und versuch es erneut.',
    crawlReplaceUrlCta: 'Andere Adresse ausprobieren',
    crawlReplaceUrlUpdating: 'Wird aktualisiert…',
    crawlReplaceUrlHelper: 'Wir entfernen den fehlgeschlagenen Versuch und starten mit dieser Adresse neu.',
    crawlRetryCta: 'Erneut versuchen',
    crawlRetrying: 'Wird erneut versucht…',
    crawlRetryHelper:
      'Wenn die Adresse stimmt und die Website nur kurz nicht erreichbar war, versuch es stattdessen erneut. ' +
      'Websites, die JavaScript brauchen, um ihren Inhalt zu zeigen, lassen sich mit einer Einstellung behandeln, ' +
      'die nach der Einrichtung auf dem Quellen-Bildschirm verfügbar ist.',
    crawlCtaSyncing: 'Deinen Bot testen, während er fertig wird →',
    crawlCtaReady: 'Weiter: deinen Bot testen →',

    tryItHeading: 'Frag ihn alles',
    tryItSubtitleWithPages: ({ n }: { n: number }) =>
      `Er kennt schon ${n} ${n === 1 ? 'Seite' : 'Seiten'} — und lernt den Rest noch dazu. Jede Antwort zeigt ihre Quellen.`,
    tryItSubtitleNoPages:
      'Er liest deine Website noch — stell trotzdem eine Frage; Antworten werden ab hier nur besser.',
    tryItSamplePrompt: `Probier: „Wie ist eure Rückgabepolitik?“`,
    tryItPlaceholder: 'Frag nach Versand, Preisen, allem…',
    tryItSending: 'Wird gesendet…',
    tryItSendCta: 'Senden',
    tryItNextCta: 'Sieht gut aus — meins machen',

    lookHeading: 'Passend zu deiner Website machen',
    lookSubtitle:
      'Wähl deine Markenfarbe — die Vorschau aktualisiert sich beim Tippen. Du kannst später alles im Design feinjustieren.',
    brandColorLabel: 'Markenfarbe',
    colorHexAriaLabel: 'Hex-Wert der Farbe',
    welcomeMessageLabel: 'Begrüßungsnachricht',
    lookSaving: 'Wird gespeichert…',
    lookContinueCta: 'Weiter',

    goLiveHeading: 'Ein Snippet, und du bist live',
    goLivePasteBeforePrefix: 'Füg das vor dem schließenden',
    goLivePasteBeforeSuffix: 'Tag auf deiner Website ein. Das war’s — der Bot erscheint unten rechts.',
    goLiveInstallGuidesLink: 'Plattform-Installationsanleitungen',
    goLiveFirstAnswerNotice: 'Wir schicken dir eine E-Mail, sobald das Widget seinem ersten Besucher antwortet.',
    goLiveDashboardCta: 'Zum Dashboard',
    goLiveViewSourcesCta: 'Quellen ansehen',
  },
  overview: {
    title: 'Übersicht',
    liveOnPrefix: 'Live auf ',
    answeringSince: ({ date }: { date: string }) => `antwortet seit ${date}`,
    testInPlaygroundCta: 'Im Playground testen',
    addSourcesCta: 'Quellen hinzufügen',
    botDetailsLoadError: ({ message }: { message: string }) => `Bot-Details konnten nicht geladen werden — ${message}`,

    messagesLabel: 'Nachrichten · letzte 14 Tage',
    conversationsLink: 'Gespräche →',
    vsPreviousPeriod: 'ggü. vorherigen 14 Tagen',
    leadsLabel: 'Erfasste Leads',
    newLeadsCount: ({ n }: { n: number }) => `${n} neu`,
    leadsThisWeek: ({ n }: { n: number }) => `${n} diese Woche`,
    viewLeadsLink: 'Leads ansehen →',
    unansweredLabel: 'Unbeantwortete Fragen',
    fixThemLink: 'Beheben →',

    knowledgeHeading: 'Wissen',
    pointAtSite: 'Auf deine Website richten',
    addSourceLink: 'Quelle hinzufügen →',
    addSourcesLink: 'Quellen hinzufügen →',
    pagesIndexed: 'Indexierte Seiten',
    sourcesCountLabel: 'Quellen',
    lastSyncedLabel: 'Zuletzt synchronisiert',
    manageSourcesLink: 'Quellen verwalten →',
    resyncing: 'Wird neu synchronisiert…',
    allSyncing: 'Alle werden synchronisiert',
    resyncCta: 'Neu synchronisieren',
    resyncNothingToSync: 'Nichts zu synchronisieren — jede Quelle wird bereits synchronisiert.',
    resyncGenericError: 'Neu-Synchronisierung konnte nicht gestartet werden — bitte versuch es erneut.',

    latestConversationsHeading: 'Neueste Gespräche',
    allConversationsLink: 'Alle Gespräche →',
    noConversationsEmpty: 'Ruhig in den letzten 14 Tagen — neue Gespräche erscheinen hier.',
    noMessageFallback: '(keine Nachricht)',
    leadCapturedTag: 'Lead erfasst',
    hasUnansweredTag: 'Hat unbeantwortete',

    knowledgeAllReady: 'Alles bereit',
    knowledgeSyncing: 'Quellen werden synchronisiert',
    knowledgeNeedsAttention: 'Eine Quelle braucht Aufmerksamkeit',
    knowledgeRemoving: 'Quellen werden entfernt',

    createdSubtitle: ({ time }: { time: string }) => `Erstellt ${time}`,
    zeroStatMessagesHint: 'Beginnt mit der ersten Frage',
    zeroStatLeadsHint: 'Name und E-Mail, mitten im Chat',
    zeroStatUnansweredHint: 'Noch nichts zu beheben',
    heroReadyHeadline: 'Dein Bot ist bereit zu antworten',
    heroIndexingHeadline: 'Liest deine Website noch',
    heroEmptyHeadline: 'Füg eine Quelle hinzu, um loszulegen',
    heroEmptyBody:
      'Er hat noch nichts zu lesen. Füg eine Quelle hinzu, und er ist innerhalb von Minuten bereit zu antworten.',
    heroIndexingErrorBody:
      'Eine Quelle braucht Aufmerksamkeit — sie wird nicht von allein fertig. Schau in der Wissen-Karte nach, was nicht stimmt, und bring den Bot dann auf deine Website, sobald es behoben ist.',
    heroIndexingPrefix: 'Er liest noch',
    heroIndexingNoSourceFallback: 'deine Quelle',
    heroIndexingSuffix: ' — schau in ein paar Minuten wieder vorbei, und bring ihn dann auf deine Website.',
    heroReadyBodyPrefix: ({ n }: { n: number }) => `Er hat ${n} ${n === 1 ? 'Seite' : 'Seiten'} gelesen`,
    heroReadyFromDomain: ' von ',
    heroReadyBodySuffix:
      ' und kennt sie gut genug, um Fragen dazu zu beantworten. Ein Schritt bleibt: bring ihn auf deine Website.',
    onlyPagesNudge: ({ n }: { n: number }) =>
      `Nur ${n} ${n === 1 ? 'Seite' : 'Seiten'}? Füg deine FAQ-, Preis- oder Richtlinienseiten hinzu, damit er mehr zum Arbeiten hat.`,
    addSourceCta: 'Quelle hinzufügen',
    addSourceHelperText:
      'Dauert etwa eine Minute. Der Embed-Schnipsel wartet genau hier, sobald er etwas zum Beantworten hat.',
    pasteBeforeBodyLabel: 'FÜG DAS VOR </BODY> EIN',
    emailSnippetLink: 'Schnipsel an meinen Entwickler mailen',
    emailSnippetSubject: ({ botName }: { botName: string }) => `${botName} auf unserer Website einbetten`,
    emailSnippetBody: ({ snippet }: { snippet: string }) => `Füg das vor dem schließenden body-Tag ein:\n\n${snippet}`,
    whatHappensNextHeading: 'Was als Nächstes passiert',
    stepVisitorAsksTitle: 'Ein Besucher fragt etwas',
    stepVisitorAsksBody: 'Die Blase sitzt unten rechts auf jeder Seite mit dem Snippet. Sonst nichts einzurichten.',
    stepEmailAnswerTitle: 'Wir mailen dir die erste Antwort',
    stepEmailAnswerBody: 'So liest du genau, was er gesagt hat, in den Worten deines Kunden, bevor es sonst jemand tut.',
    stepUnansweredTitle: 'Was er nicht beantworten konnte, landet hier',
    stepUnansweredBody: 'Er verweigert die Antwort, statt sich etwas auszudenken. Jede Verweigerung wird eine Zeile für dich zum Beheben.',
    stepAddSourceTitle: 'Quelle hinzufügen',
    stepAddSourceBody:
      'Richte ihn auf deine Website, oder lad Dateien und eingefügten Text hoch — er braucht etwas zum Lesen, bevor er antworten kann.',
    stepAddSourceVisitorTitle: 'Ein Besucher fragt, du bekommst die Antwort per E-Mail',
    countersHeading: 'ZÄHLER STARTEN MIT DEINEM ERSTEN BESUCHER',

    needsYouHeading: 'Braucht dich',
    allUnansweredLink: 'Alle unbeantworteten →',
    needsYouEmpty: 'Nichts zu beheben — dein Bot hat alles beantwortet, was gefragt wurde.',
    needsYouIntro: 'Dein Bot hat gesagt, dass er es nicht weiß. Beantworte es einmal, und er weiß es für immer.',
    askedSummary: ({ n, time }: { n: number; time: string }) =>
      `${n === 1 ? 'Einmal gefragt' : `${n}-mal gefragt`} · zuletzt ${time}`,
    writeAnswerCta: 'Antwort schreiben',
    cancelCta: 'Abbrechen',
    answerPlaceholder: 'Schreib die Antwort, die du diesem Besucher geben würdest. Der Bot nutzt sie ab jetzt.',
    addingCta: 'Wird hinzugefügt…',
    addAsQaSourceCta: 'Als Q&A-Quelle hinzufügen',
    saveAnswerError: 'Diese Antwort konnte nicht gespeichert werden — bitte versuch es erneut.',
  },
  sources: {
    pageTitle: 'Quellen',
    sourcesSubtitle: ({
      n,
      pageCount,
      lastSynced,
    }: {
      n: number;
      pageCount: number;
      lastSynced: string;
    }) =>
      `${n} ${n === 1 ? 'Quelle' : 'Quellen'} · ${pageCount} ${pageCount === 1 ? 'Seite' : 'Seiten'} · zuletzt synchronisiert ${lastSynced}`,
    botLoadError: ({ message }: { message: string }) => `Dieser Bot konnte nicht geladen werden — ${message}`,

    typeUrl: 'URL',
    typeSitemap: 'Sitemap',
    typeText: 'Text',
    typeFile: 'Datei',

    colType: 'Typ',
    colUri: 'URI',
    colStatus: 'Status',
    colRenderedMode: 'Rendermodus',
    colPages: 'Seiten',
    colLastSynced: 'Zuletzt synchronisiert',
    colActions: 'Aktionen',

    urlFieldLabel: 'Seiten-URL',
    urlPlaceholder: 'https://example.com/docs',
    sitemapFieldLabel: 'Sitemap-URL',
    sitemapPlaceholder: 'https://example.com/sitemap.xml',
    textTitleFieldLabel: 'Titel',
    textTitlePlaceholder: 'Rückgaberichtlinie',
    textContentFieldLabel: 'Inhalt',
    textContentPlaceholder: 'Füg den Text ein, den der Bot kennen soll…',
    fileFieldLabel: 'Datei',
    fileDropPrompt: 'Datei hierher ziehen, oder klicken zum Durchsuchen',
    fileDropCaption: ({ extensions, maxSize }: { extensions: string; maxSize: string }) =>
      `${extensions} · bis zu ${maxSize}`,
    fileTooLarge: ({ maxSize }: { maxSize: string }) => `Diese Datei ist größer als das Limit von ${maxSize}.`,
    fileTypeNotSupported: ({ ext, extensions }: { ext: string; extensions: string }) =>
      `${ext ? `.${ext}` : 'Diese Datei'} wird nicht unterstützt — nutz ${extensions}.`,
    filesDroppedNote: ({ name, count }: { name: string; count: number }) =>
      `„${name}“ wird verwendet — ${count} weitere ${count === 1 ? 'Datei' : 'Dateien'} abgelegt, ignoriert.`,
    fileDropNotAFile: 'Das war keine Datei — zieh stattdessen eine Datei von deinem Computer hierher.',
    fileTitleFieldLabel: 'Titel (optional)',
    fileTitlePlaceholder: 'Standardmäßig der Dateiname',

    addingCta: 'Wird hinzugefügt…',
    addSourceCta: 'Quelle hinzufügen',

    emptyTitle: 'Auf deine Website richten',
    emptyDescription: 'Füg oben eine URL, Sitemap, Text oder Datei hinzu, und wir fangen an, sie zu lesen.',

    reviewPagesLink: 'Seiten überprüfen',
    nearEmptyInfoAriaLabel: ({ count }: { count: number }) =>
      `${count} ${count === 1 ? 'Seite sah' : 'Seiten sahen'} leer aus`,

    resyncCta: 'Neu synchronisieren',
    deleteCta: 'Löschen',
    cancelCta: 'Abbrechen',
    syncInProgressError: 'Synchronisierung läuft — versuch es später erneut',

    deleteConfirmTitle: 'Diese Quelle löschen?',
    deleteConfirmDescriptionPrefix: 'Entfernt ',
    deleteConfirmDescriptionSuffix: ' und alles, was sie indexiert hat. Das kann nicht rückgängig gemacht werden.',

    renderedModeExplainerAriaLabel: 'Was ist der Rendermodus?',
    renderedModeExplainerBody1Bold: 'Der Rendermodus',
    renderedModeExplainerBody1Rest:
      ' lässt jede Seite vor dem Lesen in einem echten Browser laufen, sodass Inhalte erfasst werden, die erst nach dem Ausführen von JavaScript erscheinen.',
    renderedModeExplainerBody2:
      'Lass ihn für normale Websites aus — das ist schneller und günstiger. Schalt ihn ein, wenn eine Seite dem Crawler leer erscheint (Single-Page-Apps, Dashboards, alles, was seinen Text erst nach dem Öffnen der Seite lädt).',
    renderedModeExplainerBody3:
      'Trade-off: Der Scan ist langsamer und verbraucht pro Durchlauf mehr von deinem Seitenbudget.',

    renderedModeLabel: 'Rendermodus',
    modeRendered: 'Gerendert',
    modeStandard: 'Standard',
    switchModeTitle: ({ mode }: { mode: string }) => `Zu Modus ${mode} wechseln?`,
    switchModeDescription: ({ mode }: { mode: string }) =>
      `Das scannt die Quelle erneut im Modus ${mode}. Seiten, die anders zurückkommen, werden neu indexiert, und alle, die nicht mehr existieren, werden entfernt — der aktuelle Index bleibt verfügbar, bis der neue Scan fertig ist.`,
    switchModeCta: ({ mode }: { mode: string }) => `Zu Modus ${mode} wechseln`,
  },
  sourceDetail: {
    crawledPages: ({ n }: { n: number }) => `${n} ${n === 1 ? 'Seite' : 'Seiten'} gescannt.`,
    crawledPagesWithSkipped: ({ n, failedCount }: { n: number; failedCount: number }) =>
      `${n} ${n === 1 ? 'Seite' : 'Seiten'} gescannt. ${failedCount} ${failedCount === 1 ? 'Seite lud' : 'Seiten luden'} nicht und ${failedCount === 1 ? 'wurde' : 'wurden'} übersprungen.`,
    truncatedCapacity: ({ n }: { n: number }) =>
      `${n} ${n === 1 ? 'Seite' : 'Seiten'} gescannt, bevor unsere eigene Scan-Kapazität ausging. Das ist vorübergehend auf unserer Seite — wart ein paar Minuten und starte den Scan erneut.`,
    truncatedDeadline: ({ n }: { n: number }) =>
      `${n} ${n === 1 ? 'Seite' : 'Seiten'} gescannt, bevor dieser Scan unser eigenes Zeitlimit erreichte.`,
    truncatedAttempts: ({ n }: { n: number }) =>
      `${n} ${n === 1 ? 'Seite' : 'Seiten'} gescannt, bevor dem Scan die Versuche bei Seiten ausgingen, die nicht laden wollten.`,
    truncatedPageCap: ({ n }: { n: number }) =>
      `${n} ${n === 1 ? 'Seite' : 'Seiten'} gescannt — das Maximum, das das Seitenlimit deines Plans gerade erlaubt.`,
    remainingAtLeast: ({ n }: { n: number }) =>
      ` Mindestens ${n} ${n === 1 ? 'Seite wurde' : 'Seiten wurden'} nicht indexiert.`,
    remainingNone: ' Einige Seiten wurden möglicherweise nicht indexiert.',
    crawlFailedGeneric: 'Dieser Scan hat keine Seiten indexiert.',

    typeUrl: 'URL',
    typeSitemap: 'Sitemap',
    typeText: 'Text',
    typeFile: 'Datei',

    languagePrefix: 'Sprache: ',

    factType: 'Typ',
    factRenderedMode: 'Rendermodus',
    factLastSynced: 'Zuletzt synchronisiert',

    resyncCta: 'Neu synchronisieren',
    deleteCta: 'Löschen',

    latestCrawlHeading: 'Letzter Scan',
    neverCrawledYet: 'Diese Quelle wurde noch nicht gescannt.',
    crawlRanInMode: ({ rendered }: { rendered: boolean }) =>
      `Dieser Scan lief im Modus ${rendered ? 'Gerendert' : 'Standard'}.`,

    failedUrlsHeading: 'Fehlgeschlagene URLs',
    failedUrlsCountCaveat: ({ total, shown }: { total: number; shown: number }) =>
      ` — ${total} insgesamt, die ersten ${shown} werden angezeigt`,
    offSiteRedirectsHeading: 'Websitefremde Weiterleitungen',
    offSiteRedirectsMoreCaveat: ' — es könnten mehr sein, als diese Liste zeigt',
    nearEmptyCameBack: ({ n }: { n: number }) =>
      `${n} ${n === 1 ? 'Seite kam' : 'Seiten kamen'} mit fast keinem Inhalt zurück.`,

    pagesIndexedHeading: 'Indexierte Seiten',
    nearEmptyOnlyToggle: 'Nur fast-leere',

    noPagesIndexedYet: 'Noch keine Seiten indexiert.',
    colUrl: 'URL',
    colCharacters: 'Zeichen',
    colStatus: 'Status',
    nearEmptyBadge: 'fast leer',

    indexStateIndexed: 'Indexiert',
    indexStateIndexing: 'Wird indexiert…',
    indexStateFailed: 'Fehlgeschlagen',

    fileNoMarkdownExplainer:
      'Gespeichert als die hochgeladene Datei selbst — wir behalten das Original, nicht eine separate Textkopie, daher gibt es hier nichts zur Vorschau. Die Datei ist indexiert, und der Bot antwortet aus ihr.',
    nothingStoredForDocument: 'Für dieses Dokument ist nichts gespeichert.',

    storedContentHeading: 'Gespeicherter Inhalt',
    nothingStoredYet: 'Noch nichts gespeichert.',

    deleteDialogTitle: 'Diese Quelle löschen?',
    deleteDialogDescriptionPrefix: 'Entfernt ',
    deleteDialogDescriptionSuffix: ' und alles, was sie indexiert hat. Das kann nicht rückgängig gemacht werden.',
    cancelCta: 'Abbrechen',
  },
  conversations: {
    today: 'Heute',
    yesterday: 'Gestern',
    yourTesting: 'Deine Tests',
    visitor: 'Besucher',
    playgroundTag: 'Playground',
    noMessageFallback: '(keine Nachricht)',
    leadCapturedTag: 'Lead erfasst',
    hasUnansweredTag: 'Hat unbeantwortete',
    unansweredChipLabel: 'Unbeantwortet',
    loadingMore: 'Lädt mehr…',
    loadMore: 'Mehr laden',
    selectConversationPrompt: 'Wähl ein Gespräch aus, um sein Transkript zu sehen.',
    backToConversations: '← Zurück zu den Gesprächen',
    transcriptStartedSummary: ({ time, n }: { time: string; n: number }) =>
      `Gestartet ${time} · ${n} Nachrichten`,
    deleteConversationAriaLabel: 'Dieses Gespräch löschen',
    leadSubmittedTag: '✓ Lead übermittelt',
    cancelCta: 'Abbrechen',
    deletingCta: 'Wird gelöscht…',

    pageTitle: 'Gespräche',
    headerSubtitle: ({ n }: { n: number }) =>
      `${n} ${n === 1 ? 'Gespräch' : 'Gespräche'} · neueste zuerst`,
    emptyTitle: 'Wartet auf deinen ersten Besucher',
    emptyDescription: 'Gespräche erscheinen hier, sobald jemand mit deinem Bot chattet.',

    deleteAllCta: 'Alle Gespräche löschen',
    deleteDialogTitle: 'Dieses Gespräch löschen?',
    deleteDialogDescription:
      'Löscht dieses Transkript und seine Nachrichten dauerhaft. Ein darin erfasster Lead bleibt im Leads-Tab. Das kann nicht rückgängig gemacht werden.',
    deleteCta: 'Gespräch löschen',

    deleteAllDialogTitle: 'Alle Gespräche löschen?',
    deleteAllDescriptionPrefix:
      'Löscht dauerhaft jedes Gespräch und jede Nachricht für diesen Bot. Erfasste Leads bleiben im Leads-Tab. Das kann nicht rückgängig gemacht werden. Gib ',
    deleteAllDescriptionSuffix: ' ein, um zu bestätigen.',
    confirmBotNameAriaLabel: 'Bot-Namen bestätigen',
  },
  leads: {
    filterNew: 'Neu',
    filterContacted: 'Kontaktiert',
    filterArchived: 'Archiviert',
    filterAll: 'Alle',
    actionMarkNew: 'Als neu markieren',
    actionMarkContacted: 'Als kontaktiert markieren',
    actionArchive: 'Archivieren',
    statusNew: 'neu',
    statusContacted: 'kontaktiert',
    statusArchived: 'archiviert',

    filterByStatusAriaLabel: 'Nach Status filtern',
    pageTitle: 'Leads',
    exportingCta: 'Wird exportiert…',
    exportCsvCta: 'CSV exportieren',
    exportCsvError: 'Leads konnten nicht exportiert werden — bitte versuch es erneut.',

    headerSubtitleAll: ({ n }: { n: number }) =>
      `${n} ${n === 1 ? 'Lead' : 'Leads'} erfasst · neueste zuerst`,
    headerSubtitleFiltered: ({ n, filterLabel }: { n: number; filterLabel: string }) =>
      `${n} ${filterLabel} ${n === 1 ? 'Lead' : 'Leads'} · neueste zuerst`,

    deleteAllCta: 'Alle Leads löschen',
    bulkActionsAriaLabel: 'Massenaktionen',
    selectedCount: ({ n }: { n: number }) => `${n} ausgewählt`,
    bulkCapMessage: ({ max }: { max: number }) => `Wähl bis zu ${max} Leads gleichzeitig aus`,
    bulkUpdatedMessage: ({ n }: { n: number }) => `${n} aktualisiert`,
    bulkUpdateError: 'Diese Leads konnten nicht aktualisiert werden. Versuch es erneut.',

    emptyTitle: 'Wartet auf deinen ersten Lead',
    emptyDescription: 'Leads, die vom Chat-Widget deines Bots erfasst werden, erscheinen hier.',
    noFilterResultsTitle: ({ filterLabel }: { filterLabel: string }) => `Keine ${filterLabel} Leads`,
    noFilterResultsDescription: 'Gerade nichts in diesem Filter.',

    selectAllAriaLabel: 'Alle Leads auswählen',
    colName: 'Name',
    colContact: 'Kontakt',
    colIntent: 'Absicht',
    colStatus: 'Status',
    colCaptured: 'Erfasst',
    colActions: 'Aktionen',

    noEmailFallback: '(keine E-Mail)',
    selectLeadAriaLabel: ({ contact }: { contact: string }) => `Lead ${contact} auswählen`,
    deleteLeadAriaLabel: ({ contact }: { contact: string }) => `Lead ${contact} löschen`,
    playgroundTag: 'Playground',
    viewChatCta: 'Chat ansehen',
    loadingMoreCta: 'Lädt mehr…',
    loadMoreCta: 'Mehr laden',

    cancelCta: 'Abbrechen',
    deletingCta: 'Wird gelöscht…',

    deleteDialogTitle: 'Diesen Lead löschen?',
    deleteDescriptionPrefix: 'Löscht dauerhaft ',
    deleteDescriptionSuffix: '. Das kann nicht rückgängig gemacht werden.',
    noContactFallback: 'diesen Lead',
    deleteCta: 'Lead löschen',

    deleteAllDialogTitle: 'Alle Leads löschen?',
    deleteAllDescriptionPrefix:
      'Löscht dauerhaft jeden für diesen Bot erfassten Lead. Das kann nicht rückgängig gemacht werden. Gib ',
    deleteAllDescriptionSuffix: ' ein, um zu bestätigen.',
    confirmBotNameAriaLabel: 'Bot-Namen bestätigen',
  },
  unanswered: {
    tabOpen: 'Offen',
    tabDismissed: 'Verworfen',
    tabAnswered: 'Beantwortet',
    filterByStatusAriaLabel: 'Nach Status filtern',

    pageTitle: 'Unbeantwortet',
    headerSubtitle: ({ n, verb }: { n: number; verb: string }) =>
      `${n} ${n === 1 ? 'Frage' : 'Fragen'} ${verb}`,
    subtitleVerbOpen: 'offen',
    subtitleVerbDismissed: 'verworfen',
    subtitleVerbAnswered: 'beantwortet',

    openTabIntro:
      'Fragen, die dein Bot nicht beantworten konnte, gruppiert. Schreib einmal eine Antwort — sie wird zur Q&A-Quelle, und der Bot kennt sie für immer.',

    emptyTitleOpen: 'Nichts offen — dein Bot hat alles beantwortet, was gefragt wurde.',
    emptyTitleDismissed: 'Nichts verworfen.',
    emptyTitleAnswered: 'Noch keine beantworteten Fragen.',

    askedOnce: 'Einmal gefragt',
    askedTimes: ({ n }: { n: number }) => `${n}-mal gefragt`,

    viewSourceCta: 'Quelle ansehen',
    sourceRemoved: 'Quelle entfernt',
    viewChatCta: 'Chat ansehen',
    chatDeleted: 'Chat gelöscht',

    dismissCta: 'Verwerfen',
    reopenCta: 'Wieder öffnen',
    writeAnswerCta: 'Antwort schreiben',
    cancelCta: 'Abbrechen',
    answerPlaceholder: 'Schreib die Antwort, die du diesem Besucher geben würdest. Der Bot nutzt sie ab jetzt.',
    addingCta: 'Wird hinzugefügt…',
    addAsQaSourceCta: 'Als Q&A-Quelle hinzufügen',
    answerAddedNotice: 'Als Q&A-Quelle hinzugefügt — der Bot nutzt sie ab dem nächsten Gespräch.',
    saveAnswerError: 'Diese Antwort konnte nicht gespeichert werden — bitte versuch es erneut.',
  },
  appearance: {
    pageTitle: 'Design',
    pageSubtitle: 'Wie das Widget aussieht und Besucher auf deiner Website begrüßt',

    colorLabel: 'Farbe',
    colorHexAriaLabel: 'Hex-Wert der Farbe',
    botNameLabel: 'Bot-Name',
    botNamePlaceholder: 'Acme Support',
    welcomeMessageLabel: 'Begrüßungsnachricht',
    welcomePlaceholder: 'Hi! Wie kann ich dir heute helfen?',

    quickQuestionsLabel: 'Schnellfragen',
    addQuestionCta: 'Frage hinzufügen',
    quickQuestionsHint: 'Optional — als Chips angezeigt, die Besucher antippen können, bevor sie etwas getippt haben.',
    quickQuestionAriaLabel: ({ n }: { n: number }) => `Schnellfrage ${n}`,
    quickQuestionPlaceholder: 'Was sind eure Geschäftszeiten?',
    removeCta: 'Entfernen',

    positionLabel: 'Launcher-Position',
    positionBottomRight: 'Unten rechts',
    positionBottomLeft: 'Unten links',

    languageLabel: 'Sprache',
    autoLanguageLabel: 'An den Browser des Besuchers anpassen',

    badgeLabel: '„Powered by Embent“-Badge',
    badgeHint: 'Zeigt einen kleinen „Powered by Embent“-Link im Widget-Footer.',
    badgeSwitchAriaLabel: 'Das „Powered by Embent“-Badge anzeigen',
    badgeLoadError: 'Konnte nicht geladen werden — neu laden',
    freeBadgeNote: ({ plans }: { plans: string }) => `Ein Upgrade auf ${plans} entfernt diese Anforderung.`,

    saveCta: 'Speichern',
    savingCta: 'Wird gespeichert…',
    savedLabel: 'Gespeichert',

    livePreviewHeading: 'Live-Vorschau',
    previewCaption:
      'Nur der Launcher-Button und das Panel werden hier gerendert — die Seite drumherum ist absichtlich leer.',
    devHintPrefix: 'Erscheint nichts, ist das Widget-Bundle lokal nicht gebaut: führ aus',
    devHintIn: 'in',
    devHintAndCopy: 'aus und kopier',
    devHintTo: 'nach',

    widgetPreviewTitle: 'Widget-Vorschau',
  },
  botSettings: {
    pageTitle: 'Einstellungen',
    pageSubtitle: 'Erlaubte Domains, Lead-Erfassung, und wie sich der Bot vorstellt',

    leadCaptureOffLabel: 'Aus',
    leadCaptureOffBlurb: 'Der Bot fragt Besucher nie nach Kontaktdaten.',
    leadCaptureOnIntentLabel: 'Bei Kaufabsicht',
    leadCaptureOnIntentBlurb:
      'Der Bot fragt nur nach einer E-Mail-Adresse oder Telefonnummer, wenn er nicht antworten kann oder der Besucher ein Follow-up möchte.',
    leadCaptureAlwaysLabel: 'Immer',
    leadCaptureAlwaysBlurb:
      'Der Bot fragt einmal nach Kontaktdaten, direkt nach dem ersten Austausch in einem Gespräch.',
    leadCapturePreChatLabel: 'Formular vor dem Chat',
    leadCapturePreChatBlurb:
      'Besucher füllen ein kurzes Kontaktformular aus (Name, E-Mail, optional Telefon), bevor der Chat beginnt. Der Bot fragt danach im Chat nie wieder.',

    leadLevelSubtleLabel: 'Zurückhaltend',
    leadLevelSubtleBlurb: 'Bietet ein Follow-up nur an, wenn der Besucher darum bittet, kontaktiert zu werden, oder ein Angebot möchte.',
    leadLevelBalancedLabel: 'Ausgewogen (empfohlen)',
    leadLevelBalancedBlurb:
      'Bietet einmal an, wenn der Besucher echte Kaufsignale zeigt — fragt, wie er starten kann, möchte eine Demo, oder erwähnt die eigene Website.',
    leadLevelProactiveLabel: 'Proaktiv',
    leadLevelProactiveBlurb:
      'Bietet an, sobald der Besucher zu prüfen scheint, und fragt später noch einmal, wenn das erste Angebot unbeantwortet blieb.',

    domainRequiredError: 'Erforderlich — entfern die Zeile, statt sie leer zu lassen',
    domainBareHostnameError: 'Nur reiner Hostname, z. B. „example.com“ (kein Schema oder Pfad)',
    domainTooLongError: 'Zu lang',

    aboutLabel: 'Über diesen Bot',
    aboutHint:
      'Eine kurze Einführung, die der Bot immer kennt — wer er ist, wem er dient — sodass Fragen wie ' +
      '„wer bist du?“ sofort beantwortet werden, ohne zu suchen.',
    aboutPlaceholder:
      'Wir sind der Support-Assistent von Acme und helfen Kunden, Acme Widgets einzurichten und Probleme zu beheben.',

    allowedDomainsLabel: 'Erlaubte Domains',
    addDomainCta: 'Domain hinzufügen',
    allowedDomainsHint:
      'Websites, die das Widget dieses Bots einbetten dürfen. Leer lassen, um jede Website zu erlauben — empfohlen ' +
      'ist, das vor dem Live-Gang auf deine eigene(n) Domain(s) zu beschränken.',
    domainAriaLabel: ({ n }: { n: number }) => `Erlaubte Domain ${n}`,
    domainPlaceholder: 'example.com',
    removeCta: 'Entfernen',

    leadCaptureLabel: 'Lead-Erfassung',
    leadLevelLabel: 'Wie proaktiv?',

    saveCta: 'Speichern',
    savingCta: 'Wird gespeichert…',
    savedLabel: 'Gespeichert',

    embedSnippetHeading: 'Embed-Schnipsel',
    embedPastePrefix: 'Füg das vor dem schließenden',
    embedPasteSuffix: 'Tag auf deiner Website ein.',

    embedCopyCta: 'Kopieren',
    embedCopiedCard: 'Kopiert!',
    embedCopiedCheck: '✓ Kopiert',

    dangerZoneHeading: 'Gefahrenzone',
    dangerZoneDescription:
      'Löscht den Bot, seine Quellen, Gespräche, Leads und seinen Suchindex. Das kann nicht rückgängig gemacht werden.',
    deleteBotCta: 'Diesen Bot löschen',
    deleteBotDialogTitle: ({ name }: { name: string }) => `„${name}“ löschen?`,
    dangerZoneDialogDescriptionPrefix:
      'Löscht den Bot, seine Quellen, Gespräche, Leads und seinen Suchindex. Das kann nicht rückgängig gemacht werden. Gib ',
    dangerZoneDialogDescriptionSuffix: ' ein, um zu bestätigen.',
    confirmBotNameAriaLabel: 'Bot-Namen bestätigen',
    cancelCta: 'Abbrechen',
    deletingCta: 'Wird gelöscht…',
  },
  notifications: {
    heading: 'Benachrichtigungen',
    subheading: 'E-Mails gehen an die Kontoadresse jeder Person. Playground-Aktivität löst nie E-Mails aus.',
    noMembers: 'Keine Mitglieder.',
    saveError: 'Diese Einstellung konnte nicht gespeichert werden. Versuch es erneut.',

    roleOwner: 'Inhaber',
    roleAdmin: 'Admin',
    roleMember: 'Mitglied',
    cadenceOff: 'Aus',
    cadenceDaily: 'Täglich',
    cadenceWeekly: 'Wöchentlich',

    colMember: 'Mitglied',
    colNewLead: 'Neuer Lead',
    colUnansweredDigest: 'Unbeantwortet-Zusammenfassung',
    colCrawlFailed: 'Scan fehlgeschlagen',
    colWeeklySummary: 'Wochenzusammenfassung',

    newLeadAriaLabel: ({ email }: { email: string }) => `E-Mails für neue Leads an ${email}`,
    unansweredDigestAriaLabel: ({ email }: { email: string }) => `E-Mails für Unbeantwortet-Zusammenfassung an ${email}`,
    crawlFailedAriaLabel: ({ email }: { email: string }) => `E-Mails für fehlgeschlagene Scans an ${email}`,
    weeklySummaryAriaLabel: ({ email }: { email: string }) => `E-Mails für Wochenzusammenfassung an ${email}`,
  },
  playground: {
    pageTitle: 'Playground',
    pageSubtitle: 'Chatte mit deinem Bot wie ein Besucher — nichts hier erreicht deine Website.',
    retrievalDebugLabel: 'Retrieval-Debug',
    hideDebugCta: 'Debug ausblenden',
    newSessionCta: 'Neue Sitzung',
    emptyStateText: 'Sende eine Nachricht, um zu testen, wie dieser Bot antwortet, basierend auf seinen Quellen.',
    inputPlaceholder: 'Stell diesem Bot eine Frage…',
    stopCta: 'Stopp',
    sendCta: 'Senden',
    debugEmptyText: 'Stell eine Frage, um zu sehen, was abgerufen wird',
    genericError: 'Etwas ist schiefgelaufen.',
  },
  billing: {
    pageTitle: 'Abrechnung',
    openingCta: 'Wird geöffnet…',
    manageBillingCta: 'Abrechnung verwalten',

    memberGateTitle: 'Die Abrechnung wird vom Workspace-Inhaber oder einem Admin verwaltet.',
    memberGateDescription: 'Bitte einen Inhaber oder Admin, Änderungen am Plan dieses Workspace vorzunehmen.',

    nowOnPlan: ({ name }: { name: string }) => `Du bist jetzt im ${name}-Plan.`,
    finishingUp: 'Wir schließen das gerade ab — lad in einem Moment neu, falls sich dein Plan nicht aktualisiert.',
    confirmingWithPolar: 'Änderung erhalten — wird mit Polar bestätigt…',

    internalWorkspaceLabel: 'Interner Workspace',
    monthlyPriceLabel: ({ price }: { price: number }) => `$${price} / Monat`,
    internalWorkspaceInline: 'Keine Limits, Abrechnung gilt nicht.',
    internalWorkspaceHeading: 'Interner Workspace — keine Limits, Abrechnung gilt nicht.',
    internalWorkspaceBody:
      'Jedes Plan-Limit wird umgangen, und es gibt hier nichts zum Upgraden oder Bezahlen. Die Nutzung wird ' +
      'oben weiterhin zur Übersicht erfasst, aber nie an Polar übermittelt — dieser Workspace hat keine ' +
      'Abrechnungsidentität.',

    unlimitedWord: 'unbegrenzt',
    unlimitedParens: '(unbegrenzt)',
    ofCap: ({ cap }: { cap: string }) => `/ ${cap}`,
    internalRenewalFallback: 'Interner Workspace · keine Limits · Abrechnung gilt nicht',
    capsSuffix: ({
      bots,
      botsCount,
      messages,
      pages,
    }: {
      bots: string;
      botsCount: number | null;
      messages: string;
      pages: string;
    }) => `${bots} ${botsCount === 1 ? 'Bot' : 'Bots'} · ${messages} Nachrichten · ${pages} Seiten`,
    freeForever: ({ capsSuffix }: { capsSuffix: string }) => `Für immer kostenlos · ${capsSuffix}`,
    renewsOn: ({ date, capsSuffix }: { date: string; capsSuffix: string }) =>
      `Verlängert sich am ${date} · ${capsSuffix}`,

    cancelsAtPeriodEnd: 'Endet am Periodenende',
    planChangesPausedNotice: 'Planänderungen sind pausiert, bis die aktuelle Periode endet.',
    planChangesPausedReason:
      'Dieses Abo ist so eingestellt, dass es am Ende der Periode endet — setz es zuerst fort, um den Plan zu wechseln.',

    redirectingCta: 'Wird weitergeleitet…',
    switchingCta: 'Wird gewechselt…',
    upgradeToCta: ({ name }: { name: string }) => `Auf ${name} upgraden`,

    messagesThisMonthLabel: 'Nachrichten diesen Monat',
    pagesIndexedLabel: 'Indexierte Seiten',
    messageNoun: 'Nachricht',
    pageNoun: 'Seite',
    messagesUnit: ({ n }: { n: string }) => `${n} Nachrichten`,
    usageLimitWarning: ({ pct, noun }: { pct: number; noun: string }) =>
      `${pct}% deines ${noun}-Limits`,
    upgradeRaisesTo: ({ name, unit }: { name: string; unit: string }) =>
      ` — ${name} erhöht es auf ${unit}.`,
    limitReached: ' erreicht.',

    cancelSubscriptionCta: 'Abo kündigen',
    cancelingCta: 'Wird gekündigt…',
    cancelDialogTitle: 'Abo kündigen?',
    yourCurrentPlanFallback: 'deinem aktuellen Plan',
    cancelDialogDescription: ({
      planName,
      renewsDate,
    }: {
      planName: string;
      renewsDate: string | null;
    }) =>
      `Deine Bots laufen bis zum Ende dieser Abrechnungsperiode${
        renewsDate ? ` (${renewsDate})` : ''
      } auf ${planName} weiter — danach fällt der Workspace auf Free zurück. Das passiert nie sofort.`,
    keepSubscriptionCta: 'Abo behalten',

    changePlanFallbackTitle: 'Plan wechseln?',
    keepCurrentPlanCta: 'Aktuellen Plan behalten',
    confirmFallbackCta: 'Bestätigen',

    checkoutErrorFallback: 'Beim Starten des Checkouts ist etwas schiefgelaufen.',
    portalErrorFallback: 'Beim Öffnen der Abrechnungsverwaltung ist etwas schiefgelaufen.',
    cancelErrorFallback: 'Beim Kündigen deines Abos ist etwas schiefgelaufen.',

    currentPlanBadge: 'Aktueller Plan',
    botsUnit: ({ n }: { n: number }) => `${n} ${n === 1 ? 'Bot' : 'Bots'}`,
    seatsUnit: ({ n }: { n: number }) => `${n} ${n === 1 ? 'Sitzplatz' : 'Sitzplätze'}`,
    messagesPerMonth: ({ n }: { n: string }) => `${n} Nachrichten / Mon.`,
    pagesUnit: ({ n }: { n: string }) => `${n} Seiten`,
    badgeRequiredText: '„Powered by Embent“-Badge',
    badgeOptionalText: '„Powered by Embent“-Badge optional',
    upgradeCta: 'Upgrade',
    switchToCta: ({ name }: { name: string }) => `Zu ${name} wechseln`,

    flatPricingNote:
      'Pauschale Preise, keine Sitzplatz-Rechnerei, keine Überraschungskosten — wir warnen dich bei 80% und ' +
      'pausieren neue Antworten erst über 110%, und brechen nie ein Gespräch mitten in der Antwort ab.',

    planChangeConfirm: {
      verbUpgrade: 'upgraden',
      verbSwitch: 'wechseln',
      title: ({ verb, name }: { verb: string; name: string }) => `Zu ${name} ${verb}?`,
      confirmNow: ({ verb }: { verb: string }) => `Jetzt ${verb}`,
      continueToPolarCta: 'Weiter zu Polar',
      checkoutDescription: ({ name, price }: { name: string; price: number }) =>
        `Du wirst zu Polar weitergeleitet, um ein ${name}-Abo zu starten ($${price}/Mon.) — Polar berechnet ` +
        'dir dort den Betrag, um es zu aktivieren.',
      switchDescription: ({ name, price }: { name: string; price: number }) =>
        `Das wechselt deinen Plan sofort — deine Bots nutzen ab sofort die Limits von ${name} ` +
        `($${price}/Mon.) sofort. Polar berechnet dir heute nichts: die anteilige ` +
        'Differenz wird stattdessen deiner nächsten Rechnung angerechnet.',
    },
  },
  workspace: {
    pageTitle: 'Workspace-Einstellungen',
    loadErrorMessage: 'Dieser Workspace konnte nicht geladen werden. Lad die Seite neu, um es erneut zu versuchen.',
    notAllowedTitle: 'Nur Inhaber und Admins können Workspace-Einstellungen ändern.',
    notAllowedDescription: 'Bitte einen Inhaber oder Admin dieses Workspace, Änderungen vorzunehmen.',

    nameLabel: 'Workspace-Name',
    nameHint: ({ min, max }: { min: number; max: number }) =>
      `${min}–${max} Zeichen. Wird jedem in diesem Workspace und in Einladungs-E-Mails angezeigt.`,

    uploadLogoCta: 'Logo hochladen',
    removeLogoCta: 'Entfernen',
    logoHint: 'Quadratisch funktioniert am besten. PNG, JPEG, WebP oder GIF, bis zu 2 MB.',
    logoNotConfiguredError: 'Logo-Uploads sind in dieser Umgebung nicht konfiguriert.',
    uploadLogoErrorFallback: 'Das Logo konnte nicht hochgeladen werden. Versuch es erneut.',
    removeLogoErrorFallback: 'Das Logo konnte nicht entfernt werden. Versuch es erneut.',

    saveNameErrorFallback: 'Der Name konnte nicht gespeichert werden. Versuch es erneut.',
    saveCta: 'Speichern',
    savedLabel: 'Gespeichert',

    cropDialogTitle: 'Logo zuschneiden',
    cropDialogDescription: 'Ziehen zum Verschieben, zoomen zum Anpassen. Wird als 256×256-Quadrat gespeichert.',
    zoomLabel: 'Zoom',
    cropErrorFallback: 'Dieses Bild konnte nicht zugeschnitten werden. Probier eine andere Datei.',
    cancelCta: 'Abbrechen',
    useThisLogoCta: 'Dieses Logo verwenden',
  },
  members: {
    pageTitle: 'Mitglieder',
    membersOnlyTitle: 'Nur für Mitglieder',
    membersOnlyDescription: 'Bitte deinen Workspace-Inhaber oder einen Admin, Mitglieder zu verwalten.',
    unlimitedCapWord: 'Unbegrenzt',
    seatsSubtitle: ({
      used,
      cap,
      pending,
    }: {
      used: number;
      cap: string | number;
      pending: number;
    }) => `${used} von ${cap} Sitzplätzen genutzt${pending > 0 ? ` (${pending} eingeladen)` : ''}`,
    inviteMemberCta: 'Mitglied einladen',

    roleOwner: 'Inhaber',
    roleAdmin: 'Admin',
    roleMember: 'Mitglied',
    roleOptionMember: 'Mitglied',
    roleOptionAdmin: 'Admin',

    colName: 'Name',
    colEmail: 'E-Mail',
    colRole: 'Rolle',
    leaveWorkspaceCta: 'Workspace verlassen',
    removeCta: 'Entfernen',

    pendingInvitesHeading: 'Ausstehende Einladungen',
    noPendingInvites: 'Keine ausstehenden Einladungen.',
    inviteColEmail: 'E-Mail',
    inviteColRole: 'Rolle',
    inviteColExpires: 'Läuft ab',
    revokingCta: 'Wird widerrufen…',
    revokeCta: 'Widerrufen',

    cancelCta: 'Abbrechen',
    removingCta: 'Wird entfernt…',
    leaveDialogTitle: 'Diesen Workspace verlassen?',
    removeDialogTitle: ({ name }: { name: string }) => `${name} entfernen?`,
    leaveDialogDescription: 'Du verlierst sofort den Zugriff — ein Inhaber oder Admin kann dich später wieder einladen.',
    removeDialogDescription: ({ name }: { name: string }) => `${name} verliert sofort den Zugriff auf diesen Workspace.`,

    emailLabel: 'E-Mail',
    emailPlaceholder: 'teammate@example.com',
    roleLabel: 'Rolle',
    inviteErrorFallback: 'Beim Senden dieser Einladung ist etwas schiefgelaufen.',
    sendingInviteCta: 'Wird gesendet…',
    sendInviteCta: 'Einladung senden',

    roleChangeErrorFallback: 'Diese Rolle konnte nicht geändert werden.',
    removeErrorFallback: 'Dieses Mitglied konnte nicht entfernt werden.',
    revokeErrorFallback: 'Diese Einladung konnte nicht widerrufen werden.',
  },
  account: {
    pageTitle: 'Konto',
    loadSignInMethodsError: 'Anmeldemethoden konnten nicht geladen werden',
    emailPasswordLabel: 'E-Mail & Passwort',
    googleLabel: 'Google',

    savingCta: 'Wird gespeichert…',

    profileHeading: 'Profil',
    displayNameLabel: 'Anzeigename',
    namePlaceholder: 'Dein Name',
    emailLabel: 'E-Mail',
    saveNameErrorFallback: 'Beim Speichern deines Namens ist etwas schiefgelaufen.',
    saveCta: 'Speichern',
    savedLabel: 'Gespeichert',

    passwordHeading: 'Passwort',
    passwordMismatchError: 'Neues Passwort und Bestätigung stimmen nicht überein.',
    currentPasswordLabel: 'Aktuelles Passwort',
    newPasswordLabel: 'Neues Passwort',
    confirmNewPasswordLabel: 'Neues Passwort bestätigen',
    changePasswordErrorFallback: 'Beim Ändern deines Passworts ist etwas schiefgelaufen.',
    changePasswordCta: 'Passwort ändern',
    passwordChangedLabel: 'Passwort geändert',
  },
  adminImpersonate: {
    impersonateFailedFallback: 'Konnte diesen Nutzer nicht imitieren.',
    lookupFailedFallback: 'Dieser Nutzer konnte nicht nachgeschlagen werden.',
    userNotFound: 'Nutzerkonto nicht gefunden.',
    noOwnerToView: 'Dieser Workspace hat keinen Inhaber, dessen Ansicht angezeigt werden könnte.',
  },
  admin: {
    pagination: {
      pageOfTotal: ({
        page,
        totalPages,
        totalCount,
      }: {
        page: number;
        totalPages: number;
        totalCount: string;
      }) => `Seite ${page} von ${totalPages} · ${totalCount} insgesamt`,
      prevCta: 'Zurück',
      nextCta: 'Weiter',
    },
    workspaces: {
      crawlSignalRenderedLabel: 'Abhängig vom Scan im Rendermodus',
      crawlSignalSourcesAcrossBots: ({
        sourceCount,
        botCountLabel,
        botCount,
      }: {
        sourceCount: number;
        botCountLabel: string;
        botCount: number;
      }) =>
        `${sourceCount === 1 ? 'Quelle' : 'Quellen'} über ${botCountLabel} ${botCount === 1 ? 'Bot' : 'Bots'}`,
      crawlSignalRenderedFootnote:
        'Wie viel der Flotte auf Cloudflare Browser Rendering angewiesen ist — Kapazität, die wir heute im Workers-Free-Plan bekommen, nicht im Paid-Plan.',
      crawlSignalCapacityLabel: 'Eigenes Render-Kapazitätslimit erreicht (7 Tage)',
      crawlSignalSourceCountLabel: ({ n }: { n: number }) => `${n === 1 ? 'Quelle' : 'Quellen'}`,
      crawlSignalCapacityFootnote:
        'Eindeutige Quellen, nur unsere eigene Kapazität — nie das Plan-Limit eines Kunden. Ein abgebrochener Prozess oder ein aufgeräumter veralteter Job hinterlässt keinen Eintrag — das ist ein Minimum. Ein stetiger Anstieg bedeutet, es ist Zeit für Workers Paid ($5/Mon.).',

      emptyText: 'Noch keine Workspaces.',
      internalBadge: 'INTERN',
      cancelsAtPeriodEndHint: 'endet am Periodenende',
      colName: 'Name',
      colOwner: 'Inhaber',
      colPlan: 'Plan',
      colBots: 'Bots',
      colMembers: 'Mitglieder',
      colMsgsPerMo: 'Nachr. / Mon.',
      colPages: 'Seiten',
      colCreated: 'Erstellt',
      colActions: 'Aktionen',
      noOwnerTitle: 'Dieser Workspace hat keinen Inhaber',
      viewAsCta: 'Ansehen als',
      switchingCta: 'Wird gewechselt…',
      viewAsErrorFallback: 'Fehlgeschlagen',

      backToWorkspaces: '← Zurück zu Workspaces',
      ownerLabel: 'Inhaber: ',
      bannedBadge: 'gesperrt',
      createdLabel: ({ date }: { date: string }) => `Erstellt ${date}`,
      usageHeading: 'Nutzung',
      messagesThisMonthLabel: 'Nachrichten diesen Monat',
      pagesIndexedLabel: 'Indexierte Seiten',
      botsLabel: 'Bots',
      internalUsageNote: 'Interner Workspace — es gelten keine Limits; die Nutzung wird trotzdem oben erfasst.',
      botsHeading: 'Bots',
      noBotsYet: 'Noch keine Bots.',
      colBotName: 'Name',
      colPublicId: 'Öffentliche ID',
      colStatus: 'Status',
      colBotCreated: 'Erstellt',
      membersHeading: 'Mitglieder',
      noMembersYet: 'Keine Mitglieder.',
      colEmail: 'E-Mail',
      colMemberName: 'Name',
      colRole: 'Rolle',
      colJoined: 'Beigetreten',
      memberRoleLabel: {
        owner: 'Inhaber',
        admin: 'Admin',
        member: 'Mitglied',
      },
      subscriptionHeading: 'Abo',
      statusLabel: 'Status: ',
      periodEndLabel: 'Periodenende: ',
      cancelsAtPeriodEndLabel: 'Endet am Periodenende: ',
      yesLabel: 'Ja',
      noLabel: 'Nein',
      polarSubscriptionIdLabel: 'Polar-Abo-ID: ',
      manageInPolarLink: 'In Polar verwalten ↗',
      noSubscriptionText: 'Kein Abo — dieser Workspace ist im Free-Plan.',
      dangerZoneHeading: 'Gefahrenzone',
      internalToggleLabel: 'Interner Workspace',
      internalToggleHint: 'Keine Limits, keine Abrechnung — die Nutzung wird trotzdem erfasst, aber nie an Polar übermittelt.',
      banOwnerLabel: 'Inhaber sperren',
      banOwnerHint: 'Gesperrte Nutzer können sich nicht anmelden. Das Widget/die öffentliche API sind nicht betroffen.',
      unbanCta: 'Entsperren',
      unbanningCta: 'Wird entsperrt…',
      banCta: 'Sperren',
      demoteBeforeBanTitle: 'Stuf diesen Admin vor dem Sperren zu Nutzer zurück.',
      roleLabel: 'Rolle',
      roleHint: 'Admins können auf jeden internen Admin-Bildschirm zugreifen.',
      cannotChangeOwnRoleTitle: 'Du kannst deine eigene Rolle nicht ändern.',
      cancelCta: 'Abbrechen',
      savingCta: 'Wird gespeichert…',
      confirmCta: 'Bestätigen',
      markInternalDialogTitle: 'Diesen Workspace als intern markieren?',
      removeInternalDialogTitle: 'Internen Status entfernen?',
      markInternalDialogDescription:
        'Dieser Workspace umgeht jedes Plan-Limit (Bots, Nachrichten, Seiten) und wird nie abgerechnet. Die Nutzung wird zur Übersicht weiter erfasst, aber der nächtliche Polar-Nutzungs-Push überspringt ihn — es gibt keine Abrechnungsidentität, an die übermittelt werden könnte.',
      removeInternalDialogDescription:
        'Dieser Workspace kehrt zur normalen Plan-Durchsetzung und Abrechnung zurück (falls er ein aktives Abo hat).',
      banDialogTitle: ({ email }: { email: string | undefined }) => `${email} sperren?`,
      banDialogDescription: 'Das meldet sie sofort ab und verhindert jede zukünftige Anmeldung, bis sie entsperrt werden.',
      banReasonLabel: 'Grund (optional)',
      banReasonPlaceholder: 'Warum wird dieses Konto gesperrt?',
      banningCta: 'Wird gesperrt…',
      banUserCta: 'Nutzer sperren',
      roleDialogTitle: ({ role }: { role: 'user' | 'admin' | null }) => `Rolle zu ${role} ändern?`,
      roleDialogDescriptionAdmin: ({ email }: { email: string | undefined }) =>
        `${email} erhält Zugriff auf jeden internen Admin-Bildschirm.`,
      roleDialogDescriptionUser: ({ email }: { email: string | undefined }) =>
        `${email} verliert den Zugriff auf interne Admin-Bildschirme.`,
      updateWorkspaceErrorFallback: 'Dieser Workspace konnte nicht aktualisiert werden.',
      banUserErrorFallback: 'Dieser Nutzer konnte nicht gesperrt werden.',
      unbanUserErrorFallback: 'Dieser Nutzer konnte nicht entsperrt werden.',
      changeRoleErrorFallback: 'Die Rolle dieses Nutzers konnte nicht geändert werden.',
    },
    bots: {
      colName: 'Name',
      colPublicId: 'Öffentliche ID',
      colWorkspace: 'Workspace',
      colStatus: 'Status',
      colSources: 'Quellen',
      colLastCrawl: 'Letzter Scan',
      colInstanceId: 'Instanz-ID',
      noBotsYet: 'Noch keine Bots.',

      backToBots: '← Zurück zu Bots',
      workspaceLabel: 'Workspace: ',
      instanceLabel: ({ id }: { id: string }) => `Instanz: ${id}`,
      createdLabel: ({ date }: { date: string }) => `Erstellt ${date}`,
      openOwnerViewCta: 'Ansicht des Inhabers öffnen',
      openingCta: 'Wird geöffnet…',
      openOwnerViewErrorFallback: 'Die Ansicht des Inhabers konnte nicht geöffnet werden.',
      sourcesHeading: 'Quellen',
      noSourcesYet: 'Noch keine Quellen.',
      colUrl: 'URL',
      colType: 'Typ',
      colSourceStatus: 'Status',
      colPages: 'Seiten',
      colLastCrawled: 'Zuletzt gescannt',
      colError: 'Fehler',
      recentJobsHeading: 'Letzte Jobs',
      noJobsLinked: 'Diesem Bot sind noch keine Jobs zugeordnet.',
      colKind: 'Art',
      colJobStatus: 'Status',
      colAttempts: 'Versuche',
      colUpdated: 'Aktualisiert',
      colPayload: 'Payload',
      recentJobsFootnote:
        "Letzte Jobs werden nach bestem Ermessen über die Payload jedes Jobs zugeordnet — ein Job, der weder die ID dieses Bots noch eine seiner Quellen-IDs trägt, erscheint hier nicht, selbst wenn er diesen Bot indirekt betraf.",
    },
    jobs: {
      tabLabel: {
        all: 'Alle',
        running: 'Läuft',
        failed: 'Fehlgeschlagen',
      },
      noFailedJobs: 'Keine fehlgeschlagenen Jobs — die Warteschlange ist gesund.',
      noJobsToShow: 'Keine Jobs anzuzeigen.',
      colKind: 'Art',
      colStatus: 'Status',
      colPayload: 'Payload',
      colAttempts: 'Versuche',
      colCreated: 'Erstellt',
      colUpdated: 'Aktualisiert',
      colActions: 'Aktionen',
      retryCta: 'Wiederholen',
      retryingCta: 'Wird wiederholt…',
      cancelCta: 'Abbrechen',
      cancellingCta: 'Wird abgebrochen…',
      cancelJobCta: 'Job abbrechen',
      keepJobCta: 'Job behalten',
      cancelDialogTitle: 'Diesen Job abbrechen?',
      retryJobErrorFallback: 'Dieser Job konnte nicht wiederholt werden.',
      cancelJobErrorFallback: 'Dieser Job konnte nicht abgebrochen werden.',
      cancelDialogDescriptionListRow:
        "Das löscht die ausstehende Job-Zeile — sie wird nie laufen. Nur ausstehende Jobs können so abgebrochen werden; bei einem laufenden Job könnte der Handler bereits mittendrin sein.",

      backToJobs: '← Zurück zu Jobs',
      jobIdLabel: 'Job-ID',
      attemptsLabel: 'Versuche',
      createdLabel: 'Erstellt',
      updatedLabel: 'Aktualisiert',
      runAfterLabel: 'Ausführen nach',
      errorHeading: 'Fehler',
      payloadHeading: 'Payload',
      cancelDialogDescriptionDetailPage: 'Das löscht die ausstehende Job-Zeile — sie wird nie laufen.',
    },
    posts: {
      description:
        'Blogbeiträge, Entwürfe und veröffentlichte. Änderungen hier gehen innerhalb einer Minute nach dem Veröffentlichen live auf embent.com.',
      newPostCta: 'Neuer Beitrag',
      emptyText: 'Noch keine Beiträge — erstell den ersten.',
      colTitle: 'Titel',
      colStatus: 'Status',
      colAuthor: 'Autor',
      colPublished: 'Veröffentlicht',
      colUpdated: 'Aktualisiert',
      scheduledLabel: ({ date }: { date: string }) => `geplant · ${date}`,
    },
    postEditor: {
      backToPosts: '← Beiträge',
      newPostTitle: 'Neuer Beitrag',
      editPostTitleFallback: 'Beitrag bearbeiten',

      titleLabel: 'Titel',
      titlePlaceholder: 'Wie Embent Support-Fragen beantwortet',
      slugLabel: 'Slug',
      slugPlaceholder: 'how-embent-answers-support-questions',
      slugUrlPrefix: 'embent.com/blog/',
      excerptLabel: 'Auszug',
      excerptPlaceholder:
        'Ein oder zwei Sätze — wird auf /blog gezeigt und als Meta-Beschreibung verwendet.',
      tagsLabel: 'Tags',
      tagsPlaceholder: 'rag, support, launch',
      tagsHint: 'Kommagetrennt.',
      coverUrlLabel: 'Titelbild-URL',
      coverUrlPlaceholder: 'https://cdn.embent.com/blog/…',

      authorLabel: 'Autor',
      editAuthorCta: 'Autor bearbeiten',
      newAuthorCta: '+ Neuer Autor',
      authorSelectPlaceholder: 'Autor auswählen',
      noAuthorsYet: 'Noch keine Autoren — füg oben einen mit „+ Neuer Autor“ hinzu.',

      visibilityScheduledText: ({ date }: { date: string }) => `Geht live am ${date}`,
      visibilityLiveText: ({ date }: { date: string }) => `Veröffentlicht am ${date}`,
      visibilityDraftText: 'Noch nicht veröffentlicht',
      publishDateLabel: 'Veröffentlichungsdatum',
      publishDateHintPrefix: 'Zeiten sind in ',
      publishDateHintSuffix:
        " — der Uhrzeit dieses Browsers. Leer lassen, um sofort beim Veröffentlichen live zu gehen. Stell eine zukünftige Zeit ein, und der Beitrag bleibt bis dahin verborgen — er verschwindet aus dem Blog, seinen Tag-Zählern, der Sitemap und dem RSS-Feed, bis die Uhr aufholt. Nichts muss laufen, damit er erscheint.",

      bodyLabel: 'Inhalt (Markdown)',
      livePreviewLabel: 'Live-Vorschau',

      missingFieldLabel: {
        title: 'Titel',
        slug: 'Slug',
        excerpt: 'Auszug',
        body: 'Inhalt',
        author: 'Autor',
      },
      beforeSavingAdd: ({ fields }: { fields: string }) => `Füg vor dem Speichern hinzu: ${fields}.`,

      savingCta: 'Wird gespeichert…',
      saveChangesCta: 'Änderungen speichern',
      saveDraftCta: 'Entwurf speichern',
      unpublishingCta: 'Wird zurückgezogen…',
      schedulingCta: 'Wird geplant…',
      publishingCta: 'Wird veröffentlicht…',
      unpublishCta: 'Zurückziehen',
      scheduleCta: 'Planen',
      publishCta: 'Veröffentlichen',
      savedFlash: 'Gespeichert',

      renderingPreview: 'Wird gerendert…',
      previewFailedPrefix: 'Vorschau konnte nicht geladen werden: ',

      uploadFailedFallback: 'Upload fehlgeschlagen',
      bodyPlaceholder: 'Schreib in Markdown… füg ein Bild ein oder zieh es hierher, um es inline hochzuladen.',
      uploadingText: 'Wird hochgeladen…',
      imageUploadFailedPrefix: 'Bild-Upload fehlgeschlagen: ',

      deleteCta: 'Löschen',
      deleteDialogTitle: ({ title }: { title: string }) => `„${title}“ löschen?`,
      deleteDialogDescriptionPrefix: 'Löscht diesen Beitrag dauerhaft. Das kann nicht rückgängig gemacht werden. Gib',
      deleteDialogDescriptionSuffix: ' ein, um zu bestätigen.',
      confirmPostTitleAriaLabel: 'Beitragstitel bestätigen',
      cancelCta: 'Abbrechen',
      deletingCta: 'Wird gelöscht…',
      deletePostCta: 'Beitrag löschen',

      editAuthorDialogTitle: ({ name }: { name: string }) => `${name} bearbeiten`,
      newAuthorDialogTitle: 'Neuer Autor',
      authorDialogDescription: 'Nur echte Bylines — keine erfundenen Identitäten.',
      authorNameLabel: 'Name',
      authorNamePlaceholder: 'Johnny Bui',
      authorRoleLabel: 'Rolle',
      authorRolePlaceholder: 'Gründer',
      authorAvatarLabel: 'Avatar-URL',
      authorAvatarPlaceholder: 'https://cdn.embent.com/…',
      authorBioLabel: 'Bio',
      authorPersonaLabel: 'Persona (intern)',
      authorPersonaPlaceholder:
        'Schreibt in kurzen, direkten Sätzen. Skeptisch gegenüber Hype. Bevorzugt ein konkretes Beispiel gegenüber einem Adjektiv.',
      authorPersonaHint:
        "Eine Briefing-Notiz, um in der Stimme dieser Byline zu schreiben — nie veröffentlicht, nie den Lesern gezeigt.",
      authorSavingCta: 'Wird gespeichert…',
      authorAddingCta: 'Wird hinzugefügt…',
      saveAuthorCta: 'Autor speichern',
      addAuthorCta: 'Autor hinzufügen',

      writingGuideHeading: 'Schreibleitfaden',
      writingGuideSubtitle: 'Markdown-Unterstützung, Bilder, und ein KI-Briefing-Prompt',
      supportedMarkdownHeading: 'Unterstütztes Markdown',
      mdListHeadings: 'Überschriften, Ebenen 1 bis 6',
      mdListLists: 'Aufzählungs- und nummerierte Listen',
      mdListLinksPrefix: 'Links — ',
      mdListLinksSuffix: ', nur http(s) und mailto',
      mdListImages:
        'Bilder überall im Inhalt, auch mitten im Artikel — füg eins ein oder zieh es hierher (siehe unten)',
      mdListCode: 'Inline- und eingerahmte Codeblöcke, mit Syntax-Hervorhebung',
      mdListTables: 'Tabellen, GitHub-Format, mit Spaltenausrichtung',
      mdListBlockquotes: 'Blockzitate',
      strippedHeading: 'Was entfernt wird',
      strippedPrefix: 'Alles andere. Rohe HTML-Tags, und ',
      strippedSuffix:
        " -Inhalte speziell überleben nie — der Inhalt wird gegen eine feste Erlaubnisliste bereinigt (Überschriften, Listen, Links, Bilder, Code, Tabellen, Blockzitate, plus die Absätze/Hervorhebungen, aus denen sie gebaut sind), bevor er irgendwo gerendert wird, auch hier in der Vorschau.",
      imagesHeading: 'Bilder',
      imagesPrefix: 'Füg ein Bild in den Inhalt ein oder zieh es hierher — es wird zu cdn.embent.com hochgeladen und fügt',
      imagesSuffix:
        ' an der Cursor-Position ein, wo auch immer dein Cursor gerade ist, sodass es mitten im Absatz landen kann. Das Titelbild oben ist ein eigenes Feld — es kommt nie aus etwas, das in den Inhalt gezogen wurde.',
      copiedCta: 'Kopiert!',
      copyAiPromptCta: 'KI-Prompt kopieren',
      copiedToClipboard: 'In die Zwischenablage kopiert.',
      clipboardUnavailable:
        "Die Zwischenablage ist in diesem Browser nicht verfügbar — wähl den Prompt unten aus und kopier ihn.",
      assemblesPrefix: 'Stellt den Hausstil zusammen, ',
      assemblesAuthorPersonaClause: ({ authorName }: { authorName: string }) =>
        `die Persona von ${authorName}`,
      assemblesNoAuthorClause: 'einen Hinweis, dass kein Autor ausgewählt ist',
      assemblesSuffix:
        ', die Markdown-Konventionen oben, und eine gewünschte Struktur — füg es in ein externes Modell ein, dann füg den Entwurf zurück in den Inhalt ein.',

      aiPromptPersonaWithVoice: ({ authorName, persona }: { authorName: string; persona: string }) =>
        `Schreib in der Stimme von ${authorName}:\n${persona}`,
      aiPromptPersonaNoPersona: ({ authorName }: { authorName: string }) =>
        `${authorName} hat keine Persona festgelegt — schreib in einer schlichten, direkten Stimme.`,
      aiPromptAuthorFallbackLower: 'dieser Autor',
      aiPromptAuthorFallbackUpper: 'Dieser Autor',
      aiPromptTemplate: ({ personaSection }: { personaSection: string }) =>
        `Du verfasst einen Blogbeitrag für Embent (embent.com), eine KI-Agent-Plattform für Websites.

Hausstil:
- Direkt und konkret. Kein Hype, kein Marketing-Geschwafel.
- Kurze Absätze (2-4 Sätze).
- Erfinde nie Statistiken oder Kundennamen. Wenn du keine echte Zahl oder einen echten Kunden hast, denk dir keinen aus.

Autorenstimme:
${personaSection}

Markdown-Konventionen (das ist ALLES, was gerendert wird — alles andere wird entfernt):
- Überschriften (#, ##, ###), Aufzählungs- und nummerierte Listen, Blockzitate.
- Links als [Text](url) — nur http(s) und mailto.
- Bilder überall im Inhalt, auch mitten im Artikel, als ![alt](url).
- Inline- und eingerahmte Codeblöcke.
- Tabellen (GitHub-Format).
- Rohes HTML und <script>/<style>-Tags werden komplett entfernt — schreib nur reines Markdown, kein HTML.

Gewünschte Struktur:
1. Titel
2. Ein Absatz als Aufhänger
3. 4-7 Abschnitte, jeweils mit einer h2 (##) Überschrift
4. Ein kurzer Abschluss

Schreib jetzt den vollständigen Entwurf als Markdown.`,
    },
    feedback: {
      filterLabel: {
        unhandled: 'Unbehandelt',
        all: 'Alle',
      },
      typeLabel: {
        bug: 'Fehler',
        feature: 'Feature',
        question: 'Frage',
      },
      mailtoSubject: ({ id, type }: { id: string; type: string }) =>
        `Re: [Embent Feedback ${id}] ${type}`,
      replyCta: 'Antworten',
      colDate: 'Datum',
      colWorkspace: 'Workspace',
      colUser: 'Nutzer',
      colType: 'Typ',
      colMessage: 'Nachricht',
      colActions: 'Aktionen',
      emptyUnhandled: 'Kein unbehandeltes Feedback — Postfach leer.',
      emptyAll: 'Noch kein Feedback.',
      updateFeedbackErrorFallback: 'Dieses Feedback konnte nicht aktualisiert werden.',
      reopeningCta: 'Wird wieder geöffnet…',
      markingCta: 'Wird markiert…',
      reopenCta: 'Wieder öffnen',
      markHandledCta: 'Als behandelt markieren',
      noBotFallback: 'kein Bot',
      noUaFallback: 'kein UA',
    },
    playground: {
      demosAllTimeLabel: 'Demos insgesamt',
      demos30dLabel: 'Demos · 30 Tage',
      messagesAllTimeLabel: 'Nachrichten insgesamt',
      signedUpAllTimeLabel: 'Registriert',
      signedUp30dLabel: 'Registriert · 30 Tage',
      leadsAllTimeLabel: 'Erfasste Leads',
      leads30dLabel: 'Leads · 30 Tage',
      demosPerDayLabel: 'Demos pro Tag',
      messagesPerDayLabel: 'Nachrichten pro Tag',
      demoUnit: ({ count }: { count: number }): string => (count === 1 ? 'Demo' : 'Demos'),
      messageUnit: ({ count }: { count: number }): string => (count === 1 ? 'Nachricht' : 'Nachrichten'),
      noDemos30d: 'Keine Demos in den letzten 30 Tagen.',
      statusBreakdownHeading: 'Status-Aufschlüsselung (30 Tage)',
      recentDomainsHeading: 'Letzte Domains',
      noDemosYet: 'Noch keine Demos.',
      colHostname: 'Hostname',
      colStatus: 'Status',
      colPages: 'Seiten',
      colMessages: 'Nachrichten',
      colAttempts: 'Versuche',
      colSignedUp: 'Registriert',
      colLeads: 'Leads',
      colCreated: 'Erstellt',
      signedUpAriaLabel: 'registriert',
      notSignedUpAriaLabel: 'nicht registriert',
      noEmailFallback: '(keine E-Mail)',
    },
  },
} satisfies Catalog;
